import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, BLOOD_GROUPS, WARDS, isDonorAvailable, daysUntilEligible } from '../lib/supabase';

export default function Donors() {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();

  const initGroup = searchParams.get('group') || '';
  const [selectedGroup, setSelectedGroup] = useState(initGroup);
  const [selectedWard, setSelectedWard] = useState('');
  const [availableOnly, setAvailableOnly] = useState(true);

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    try {
      // Never select phone — keep it private
      let q = supabase
        .from('donors')
        .select('id, name, blood_group, ward, area, age, last_donated, is_available, donated_count, points, created_at')
        .order('created_at', { ascending: false });
      // Note: availableOnly filter is done client-side so the 56-day rule is applied correctly
      if (selectedGroup) q = q.eq('blood_group', selectedGroup);
      if (selectedWard) q = q.ilike('ward', `%${selectedWard}%`);

      const { data } = await q;
      setDonors(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, selectedWard]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  // Apply availableOnly filter client-side using the 56-day rule
  const filtered = donors.filter(d => {
    if (availableOnly && !isDonorAvailable(d)) return false;
    if (!search) return true;
    return (
      d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.area?.toLowerCase().includes(search.toLowerCase())
    );
  });

  function formatDate(date) {
    if (!date) return null;
    return new Date(date).toLocaleDateString('ml-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return (
    <div className="page">
      <div className="section-header">
        <div>
          <div className="section-title">ദാതാക്കളുടെ ഡയറക്ടറി</div>
          <div className="section-sub">
            {loading ? 'ലോഡ് ചെയ്യുന്നു…' : `${filtered.length} ദാതാക്കൾ`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          className="filter-input"
          type="text"
          placeholder="🔍 പേര് / സ്ഥലം തിരയൂ…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <select
          className="filter-select"
          value={selectedGroup}
          onChange={e => setSelectedGroup(e.target.value)}
        >
          <option value="">എല്ലാ ഗ്രൂപ്പും</option>
          {BLOOD_GROUPS.map(g => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>

        <select
          className="filter-select"
          value={selectedWard}
          onChange={e => setSelectedWard(e.target.value)}
        >
          <option value="">എല്ലാ വാർഡും</option>
          {WARDS.map(w => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={availableOnly}
            onChange={e => setAvailableOnly(e.target.checked)}
            style={{ accentColor: 'var(--red)', width: 16, height: 16 }}
          />
          ലഭ്യമായവർ മാത്രം
        </label>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          ദാതാക്കളെ ലോഡ് ചെയ്യുന്നു…
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🩸</div>
          <div className="empty-title">ദാതാക്കളൊന്നും കണ്ടെത്തിയില്ല</div>
          <div className="empty-sub">ഫിൽട്ടർ മാറ്റി ശ്രമിക്കൂ</div>
        </div>
      ) : (
        <div className="donor-grid">
          {filtered.map(donor => {
            const available = isDonorAvailable(donor);
            const daysLeft = daysUntilEligible(donor.last_donated);
            return (
              <div key={donor.id} className="donor-card">
                <div
                  className="blood-badge blood-badge-lg"
                  style={{ background: getGroupColor(donor.blood_group) }}
                >
                  {donor.blood_group}
                </div>
                <div className="donor-info">
                  <div className="donor-name">{donor.name}</div>
                  <div className="donor-meta">📍 {donor.ward}{donor.area ? ` · ${donor.area}` : ''}</div>
                  {donor.age && <div className="donor-meta">🎂 {donor.age} വയസ്സ്</div>}
                  {donor.last_donated && (
                    <div className="donor-meta">🩸 അവസാനം: {formatDate(donor.last_donated)}</div>
                  )}

                  <div className="donor-tags">
                    {available ? (
                      <span className="tag tag-green">✓ ലഭ്യം</span>
                    ) : (
                      <span className="tag tag-orange">
                        ⏳ {daysLeft > 0 ? `${daysLeft} ദിവസം കൂടി` : 'ലഭ്യമല്ല'}
                      </span>
                    )}
                    {donor.donated_count > 0 && (
                      <span className="tag">{donor.donated_count}× ദാനം</span>
                    )}
                  </div>

                  {/* Phone hidden — user must go to Request page to get connected */}
                  <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-3)', textAlign: 'center' }}>
                    🔒 ഫോൺ നമ്പർ സ്വകാര്യമാണ് — രക്തം ആവശ്യമുണ്ടെങ്കിൽ അഭ്യർത്ഥന ചേർക്കൂ
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getGroupColor(group) {
  const colors = {
    'A+': '#c0392b', 'A-': '#96281b',
    'B+': '#d35400', 'B-': '#b03a00',
    'AB+': '#6c3483', 'AB-': '#512e5f',
    'O+': '#1a5276', 'O-': '#154360',
  };
  return colors[group] || '#c0392b';
}
